import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { BULK_ACTION_ENUM } from '@/shared/model';
import { handleSwapsMultipleBeats } from '@/widgets/beats/ui/rrInterval/handler';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectActiveTab,
  selectBeatChanges,
  selectBulkSwapBeats,
  selectResetSwapBeats,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  selectSelectedTemplate,
  selectSwapBeatChanges,
  selectTemplateChanges,
  selectBulkBeatsChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { handleUpdateBulkActionSwap } from './helper';
import type { SwapTemplateModalProps } from './swapTemplateModal';

/**
 * Drives the modal that reassigns selected beats to a different morphology template on the beats tab. Merges the single and multi-selected strips, overlays pending template changes onto the active tab's original template overview for the grid, closes on the Escape key while the modal is open, and routes a chosen template into the single, bulk or by-id swap path.
 *
 * @param props - Modal props carrying isOpen and the isSwapAllTemplate and isSwapAllTemplateById mode flags.
 * @returns An object carrying mergeTemplateList, ecgDataMap and the onClickSwapTemplate and onCloseModal handlers.
 */
const useSwapTemplateModal = ({
  isSwapAllTemplate,
  isSwapAllTemplateById,
  isOpen,
}: SwapTemplateModalProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab's modal control flags back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement control-modal flags, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads/writes beat-only fields via mergeSelectedStrip - pre-existing gap.
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil)) as any;
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const mergeSelectedStrip = _.filter(
    _.uniqBy([selectedStrip || {}, ...(selectedMultipleStrip || [])], 'id'),
    (x) => !_.isEmpty(x),
  );
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const templateChanges = useReportStore(selectTemplateChanges(keyRecoil));
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Records a beat-option snapshot for the beats tab so the template swap can be undone and redone as one step. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement options payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setBulkActionSwapAction = useReportStore((s) => s.actions.setBulkActionSwap);
  /**
   * Writes the template queued for a deferred bulk swap on the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement bulk swap target, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBulkActionSwap = (v: Parameters<typeof setBulkActionSwapAction>[1]) =>
    setBulkActionSwapAction(keyRecoil, v);
  const setBulkActionAction = useReportStore((s) => s.actions.setBulkAction);
  /**
   * Writes the beats tab's queued bulk action back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement bulk action, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);

  /**
   * Reads the pending beat edits for the beats tab out of the report store. Applies the tab-scoped selectBeatChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of pending beat changes for the beats tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the pending beat edits for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of beat changes, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  /**
   * Reads the bulk beat edits that a later action must undo on the beats tab out of the report store. Applies the tab-scoped selectResetBulkBeatsChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The reset payload for bulk beat changes.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the bulk beat edits a later action must undo on the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement reset payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  /**
   * Reads the pending bulk beat edits for the beats tab out of the report store. Applies the tab-scoped selectBulkBeatsChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The bulk beat-change payload for the beats tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBulkBeatsChangesAction = useReportStore((s) => s.actions.setBulkBeatsChanges);
  /**
   * Writes the pending bulk beat edits for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement bulk beat-change payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBulkBeatsChanges = (v: Parameters<typeof setBulkBeatsChangesAction>[1]) =>
    setBulkBeatsChangesAction(keyRecoil, v);

  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Writes the pending per-beat template swaps for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of swap beat changes, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(keyRecoil, v);
  /**
   * Reads the pending per-beat template swaps for the beats tab out of the report store. Applies the tab-scoped selectSwapBeatChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of pending swap beat changes.
   */
  const getSwapBeatChanges = () => selectSwapBeatChanges(keyRecoil)(useReportStore.getState());
  const setBulkSwapBeatsAction = useReportStore((s) => s.actions.setBulkSwapBeats);
  /**
   * Writes the beats queued for a bulk template swap on the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement bulk swap payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBulkSwapBeats = (v: Parameters<typeof setBulkSwapBeatsAction>[1]) =>
    setBulkSwapBeatsAction(keyRecoil, v);
  /**
   * Reads the template swaps that a later action must undo on the beats tab out of the report store. Applies the tab-scoped selectResetSwapBeats selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of swaps queued for reset.
   */
  const getResetSwapBeats = () => selectResetSwapBeats(keyRecoil)(useReportStore.getState());
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Writes the template swaps a later action must undo on the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of swaps queued for reset, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(keyRecoil, v);
  /**
   * Reads the beats queued for a bulk template swap on the beats tab out of the report store. Applies the tab-scoped selectBulkSwapBeats selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The bulk swap payload for the beats tab.
   */
  const getBulkSwapBeats = () => selectBulkSwapBeats(keyRecoil)(useReportStore.getState());

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const selectedTemplate = useReportStore(selectSelectedTemplate(keyRecoil));
  const listSelectedCells = useReportStore((s) => s.listSelectedCells);

  const originalTemplateOverview = useReportStore((s) => s.originalTemplateOverview);

  const mergeTemplateList = useMemo(() => {
    const morphology = originalTemplateOverview[activeButton] || [];
    return _.map(morphology, (x) => {
      const findChange = _.find(templateChanges, (change) => x.id === change.id);
      return findChange || x;
    });
  }, [templateChanges, activeButton, originalTemplateOverview]);

  /**
   * Closes the swap-template modal. Resets the beats tab's modal control flags with isShowSwapTemplate off.
   *
   * @returns Nothing.
   */
  const onCloseModal = () => {
    // IControlModal (Task 7) never declares isShowSwapTemplate - pre-existing, documented in Task 7's commit notes.
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowSwapTemplate: false } as any);
  };

  /**
   * Reassigns every beat of the selected template cells to a new template, keyed by template id. Clears any queued bulk action, bails out when neither a cell selection nor a selected template exists, otherwise recomputes the beat changes, bulk beat changes, swap changes and both reset lists through handleUpdateBulkActionSwap, commits them, records the set as one undoable beat-option snapshot and closes the modal.
   *
   * @param template - Template the selected beats are reassigned to.
   * @returns Nothing.
   */
  const handleChangeBulkSwapById = (template: any) => {
    setBulkAction(BULK_ACTION_ENUM.NOTHING);
    setBulkActionSwap((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      return [];
    });
    const currentListCells = _.cloneDeep(listSelectedCells);
    if (_.isEmpty(currentListCells) && _.isEmpty(selectedTemplate)) {
      onCloseModal();
      return;
    }
    const {
      cloneBeatChanges,
      cloneBulkBeatChange,
      cloneResetBeats,

      cloneSwapBeatChanges,
      cloneBulkSwapChanges,
      cloneResetSwapList,
    } = handleUpdateBulkActionSwap({
      activeButton,
      bulkBeatsChange: getBulkBeatsChanges(),
      beatChanges: getBeatChanges(),
      resetBulkBeatsChanges: getResetBulkBeatsChanges(),

      swapBeatChanges: getSwapBeatChanges(),
      bulkSwapChanges: getBulkSwapBeats(),
      resetBulkSwapChanges: getResetSwapBeats(),

      listSelectedCells: currentListCells,
      selectedTemplate,
      newTemplateInfo: template,
    });

    setSwapBeatChanges(cloneSwapBeatChanges);
    setBulkSwapBeats(cloneBulkSwapChanges);
    setResetSwapBeats(cloneResetSwapList);
    // beat changes
    setBeatChanges(cloneBeatChanges);
    setBulkBeatsChanges(cloneBulkBeatChange);
    setResetBulkBeatsChanges(cloneResetBeats);

    setBeatOptions((prev: any) => ({
      ...prev,
      swapBeatChangesState: cloneSwapBeatChanges,
      bulkSwapBeatsState: cloneBulkSwapChanges,
      resetSwapBeatsState: cloneResetSwapList,

      beatChangesState: cloneBeatChanges,
      bulkBeatsChangesState: cloneBulkBeatChange,
      resetBulkBeatsChangesState: cloneResetBeats,
    }));
    onCloseModal();
  };

  /**
   * Applies the reviewer's template pick along whichever swap path the modal was opened in. Delegates to the by-id bulk swap when isSwapAllTemplateById is set, queues a deferred SWAP_TEMPLATE bulk action when isSwapAllTemplate is set, and otherwise recomputes beat changes, swap changes and both reset lists for the merged strip selection through handleSwapsMultipleBeats, records them as one undoable snapshot and closes the modal.
   *
   * @param template - Template the reviewer picked from the grid.
   * @returns Nothing.
   */
  const onClickSwapTemplate = (template: any) => {
    if (isSwapAllTemplateById) {
      handleChangeBulkSwapById(template);
      return;
    }
    if (isSwapAllTemplate) {
      setBulkActionSwap(template);
      setBulkAction(BULK_ACTION_ENUM.SWAP_TEMPLATE);
      onCloseModal();
      return;
    }
    const { newBeatChanges, newResetBulksBeatChanges, newSwapBeatChanges, newResetBulkSwapBeats } =
      handleSwapsMultipleBeats({
        selectedBeats: mergeSelectedStrip,
        bulkBeatsChanges: getBulkBeatsChanges(),
        beatChanges: getBeatChanges(),
        resetBulksBeatChanges: getResetBulkBeatsChanges(),
        bulkSwapBeats: getBulkSwapBeats(),
        swapBeatChanges: getSwapBeatChanges(),
        resetBulkSwapBeats: getResetSwapBeats(),
        targetTemplateId: template?.id,
        type: activeButton,
      });
    setBeatChanges(newBeatChanges);
    setResetBulkBeatsChanges(newResetBulksBeatChanges);
    setSwapBeatChanges(newSwapBeatChanges);
    setResetSwapBeats(newResetBulkSwapBeats);
    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: newBeatChanges,
      resetBulkBeatsChangesState: newResetBulksBeatChanges,
      swapBeatChangesState: newSwapBeatChanges,
      resetSwapBeatsState: newResetBulkSwapBeats,
    }));
    onCloseModal();
  };

  /**
   * Closes the swap-template modal on the Escape key. Stops the key event propagating to the ECG viewer's shortcuts and closes the modal when the key code is 27.
   *
   * @param e - Keyboard event captured on the window while the modal is open.
   * @returns Nothing.
   */
  const handleKeyUp = (e: any) => {
    const { keyCode } = e;
    e.stopPropagation();
    if (keyCode === 27) {
      // *: ESC key
      onCloseModal();
    }
  };

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keyup', handleKeyUp);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isOpen]);

  return {
    mergeTemplateList,
    ecgDataMap,
    onClickSwapTemplate,
    onCloseModal,
  };
};

export default useSwapTemplateModal;
